import { EventType } from "@/analytics/analytics-events";
import {
  FaroIconButton,
  FaroText,
  grey,
  ThumbsDownFilledIcon,
  ThumbsDownIcon,
  ThumbsUpFilledIcon,
  ThumbsUpIcon,
  type FaroIconButtonProps,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import type { ConversationFeedback } from "@faro-lotv/service-wires";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Collapse } from "@mui/material";
import { Stack, styled } from "@mui/system";
import { isReasoningUIPart, isTextUIPart, type UIMessage } from "ai";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type MouseEvent,
} from "react";
import { AiMarkdown } from "./ai-markdown";
import { FeedbackPopover } from "./feedback-popover";

type AssistantOutputProps = {
  /** The message to handle */
  message: UIMessage;

  /** Callback for when the user votes on a response */
  onVote(feedback: ConversationFeedback): Promise<void>;
};

/** Minimum time (ms) the thank-you message is shown after submitting feedback */
const FEEDBACK_MIN_DISPLAY_MS = 4000;

/**
 * @returns the output of an AI assistant message, including optional reasoning and text parts.
 * If a reasoning part is present, it is displayed in a collapsible "Thinking..." section
 * that automatically collapses once the actual answer (text part) arrives.
 * Once the response is complete, thumbs up/down buttons are shown to collect user feedback
 * via a popover with sentiment-specific tag options and an optional comment.
 */
export function AssistantOutput({
  message,
  onVote,
}: AssistantOutputProps): JSX.Element {
  const reasoning = message.parts.find(isReasoningUIPart);
  const response = message.parts.find(isTextUIPart);

  const [feedbackAnchor, setFeedbackAnchor] =
    useState<HTMLButtonElement | null>(null);
  const [selectedSentiment, setSelectedSentiment] =
    useState<ConversationFeedback["sentiment"]>();

  const openFeedback = useCallback(
    (
      event: MouseEvent<HTMLButtonElement>,
      sentiment: ConversationFeedback["sentiment"],
    ) => {
      // Stop propagation to prevent the popover's ClickAwayListener
      // from closing it when switching between thumbs up/down
      event.stopPropagation();

      const isNewSentiment = sentiment !== selectedSentiment;
      setSelectedSentiment(sentiment);
      setFeedbackAnchor(event.currentTarget);

      // Submit immediately when the user clicks a new sentiment
      if (isNewSentiment) {
        onVote({
          sentiment,
          messageId: message.id,
        });
      }
    },
    [selectedSentiment, onVote, message.id],
  );

  const [feedbackKey, setFeedbackKey] = useState(0);
  const closeFeedback = useCallback(() => {
    setFeedbackAnchor(null);

    // Increment key to remount FeedbackPopover with fresh state after close
    setFeedbackKey((k) => k + 1);
  }, []);

  const onFeedbackSubmit = useCallback(
    async (
      comment: ConversationFeedback["comment"],
      tags: ConversationFeedback["tags"],
    ) => {
      if (!selectedSentiment) return;
      await Promise.all([
        onVote({
          sentiment: selectedSentiment,
          messageId: message.id,
          comment,
          tags,
        }),
        new Promise((resolve) => setTimeout(resolve, FEEDBACK_MIN_DISPLAY_MS)),
      ]);
      closeFeedback();
    },
    [closeFeedback, selectedSentiment, message, onVote],
  );

  return (
    <Box component="div" sx={{ pb: 4, pt: 2 }}>
      {reasoning && (
        <Reasoning isThinking={!response}>{reasoning.text}</Reasoning>
      )}

      {response && <AiMarkdown>{response.text}</AiMarkdown>}

      {response?.state === "done" && (
        <Stack direction="row">
          <FaroIconButton
            size="m"
            onClick={(event) => openFeedback(event, "Positive")}
          >
            {selectedSentiment === "Positive" ? (
              <ThumbsUpFilledIcon />
            ) : (
              <ThumbsUpIcon />
            )}
          </FaroIconButton>
          <FaroIconButton
            size="m"
            onClick={(event) => openFeedback(event, "Negative")}
          >
            {selectedSentiment === "Negative" ? (
              <ThumbsDownFilledIcon />
            ) : (
              <ThumbsDownIcon />
            )}
          </FaroIconButton>
        </Stack>
      )}

      <FeedbackPopover
        key={feedbackKey}
        anchorEl={feedbackAnchor}
        open={!!feedbackAnchor}
        sentiment={selectedSentiment}
        onSubmitFeedback={onFeedbackSubmit}
        onClose={closeFeedback}
      />
    </Box>
  );
}

type ReasoningProps = {
  children: string;
  isThinking: boolean;
};

function Reasoning({ children, isThinking }: ReasoningProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const thinkingLabel = useThinkingLabel(children, isThinking);

  const toggleExpansion = useCallback(() => {
    setExpanded((nowExpanded) => {
      Analytics.track(
        nowExpanded ? EventType.closeAiReasoning : EventType.openAiReasoning,
      );

      return !nowExpanded;
    });
  }, []);

  let heading = thinkingLabel;
  if (!isThinking) {
    heading = expanded ? "Hide thinking" : "Show thinking";
  }

  return (
    <Stack>
      <Stack
        direction="row"
        onClick={toggleExpansion}
        sx={{
          cursor: "pointer",
        }}
      >
        <FaroText
          variant="bodyS"
          sx={{
            alignContent: "center",
            ...(isThinking && {
              // Adding a shimmer effect while the AI reasoning is running
              background: `linear-gradient(90deg, ${grey[400]} 1%, ${grey[800]} 50%, ${grey[400]} 99%)`,
              backgroundSize: "400% 100%",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              animation: "shimmer 5s infinite linear",
              "@keyframes shimmer": {
                from: { backgroundPosition: "200% center" },
                to: { backgroundPosition: "-200% center" },
              },
            }),
          }}
        >
          {heading}
        </FaroText>
        <ExpandMore
          expand={expanded}
          aria-expanded={expanded}
          aria-label="show more"
        >
          <ExpandMoreIcon />
        </ExpandMore>
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <AiMarkdown isReasoning>{children}</AiMarkdown>
      </Collapse>
    </Stack>
  );
}

/** Minimum interval (ms) between thinking-label updates while streaming */
const THINKING_LABEL_THROTTLE_MS = 800;

/** Time (ms) after which the "taking longer than usual" fallback label is shown */
const THINKING_OVERDUE_TIMEOUT_MS = 60_000;

/** Label shown when reasoning has been running longer than {@link THINKING_OVERDUE_TIMEOUT_MS} */
const THINKING_OVERDUE_LABEL = "This is taking longer than usual";

const BOLD_MARKDOWN_REGEX = /\*\*([^*]+)\*\*/g;

/**
 * Derives a smoothly-updating label for the thinking header while `isThinking` is true.
 * - Starts at "Thinking..."
 * - Switches to the last `**bold header**` found in `text`, throttled to at most one
 *   update per {@link THINKING_LABEL_THROTTLE_MS} (a trailing update always fires so
 *   the latest header is never permanently skipped).
 * - After {@link THINKING_OVERDUE_TIMEOUT_MS} switches to {@link THINKING_OVERDUE_LABEL}.
 *
 * @param text - The full reasoning text to scan for bold headers.
 * @param isThinking - Whether the AI is currently reasoning.
 * @returns A formatted label string for the thinking heading.
 */
function useThinkingLabel(text: string, isThinking: boolean): string {
  const [label, setLabel] = useState("Thinking...");
  const [isOverdue, setIsOverdue] = useState(false);
  const lastUpdateRef = useRef(0);
  const pendingRef = useRef<string | null>(null);
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScannedIndexRef = useRef(0);

  // Reset all label state between thinking sessions and start the overdue timer.
  useEffect(() => {
    if (!isThinking) {
      setIsOverdue(false);
      setLabel("Thinking...");
      lastUpdateRef.current = 0;
      pendingRef.current = null;
      lastScannedIndexRef.current = 0;
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
        throttleTimerRef.current = null;
      }
      return;
    }

    const timer = setTimeout(
      () => setIsOverdue(true),
      THINKING_OVERDUE_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [isThinking]);

  // Clear the throttle timer when the overdue state kicks in.
  useEffect(() => {
    if (!isOverdue) return;
    if (throttleTimerRef.current) {
      clearTimeout(throttleTimerRef.current);
      throttleTimerRef.current = null;
      pendingRef.current = null;
    }
  }, [isOverdue]);

  // Throttle updates to the label so it transitions smoothly while streaming.
  // Only scans newly appended content (from lastScannedIndexRef) to avoid re-scanning
  // already-processed text on every streaming update.
  useEffect(() => {
    if (!isThinking || isOverdue) return;

    let lastMatch: RegExpExecArray | null = null;
    let m: RegExpExecArray | null;
    const newContent = text.slice(lastScannedIndexRef.current);
    while ((m = BOLD_MARKDOWN_REGEX.exec(newContent)) !== null) lastMatch = m;
    if (!lastMatch) return;

    // Advance past the last complete match so we don't re-scan it next time.
    lastScannedIndexRef.current += lastMatch.index + lastMatch[0].length;

    const newLabel = `${lastMatch[1]}...`;
    const now = Date.now();
    const elapsed = now - lastUpdateRef.current;

    if (elapsed >= THINKING_LABEL_THROTTLE_MS) {
      // Throttle window has passed – update immediately
      lastUpdateRef.current = now;
      setLabel(newLabel);
      pendingRef.current = null;
    } else {
      // Still within the throttle window – buffer the latest label
      pendingRef.current = newLabel;
      if (!throttleTimerRef.current) {
        // Schedule one trailing update for when the window expires
        throttleTimerRef.current = setTimeout(() => {
          if (pendingRef.current) {
            lastUpdateRef.current = Date.now();
            setLabel(pendingRef.current);
            pendingRef.current = null;
          }
          throttleTimerRef.current = null;
        }, THINKING_LABEL_THROTTLE_MS - elapsed);
      }
    }
    // No cleanup here – timer teardown is handled by the isThinking/isOverdue effects
    // and unmount cleanup, so streaming text updates don't cancel the scheduled timer.
  }, [text, isThinking, isOverdue]);

  // Clean up any pending throttle timer when the hook unmounts.
  useEffect(
    () => () => {
      if (throttleTimerRef.current) clearTimeout(throttleTimerRef.current);
    },
    [],
  );

  return isOverdue ? THINKING_OVERDUE_LABEL : label;
}

interface ExpandMoreProps extends FaroIconButtonProps {
  expand: boolean;
}

// eslint-disable-next-line unused-imports/no-unused-vars
const ExpandMore = styled(({ expand, ...other }: ExpandMoreProps) => (
  <FaroIconButton {...other} />
))(({ theme }) => ({
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    {
      props: ({ expand }: ExpandMoreProps) => !expand,
      style: {
        transform: "rotate(0deg)",
      },
    },
    {
      props: ({ expand }: ExpandMoreProps) => expand,
      style: {
        transform: "rotate(180deg)",
      },
    },
  ],
}));
